/**
 * Page vitrine FTPE Outremer
 * Fédération des Très Petites Entreprises - Outremer
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Users, FileText, Award, Target, Heart, ArrowRight, CheckCircle, Star, MapPin, Mail, Landmark, ClipboardCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import BackButton from '../components/BackButton';
import TerritoiresWatermark from './FTPETerritoiresWatermark';
import { FTPE_LOGO, MISSIONS, VALEURS, PUBLICS_CIBLES, AVANTAGES_ADHESION, ETAPES_ADHESION, DOCUMENTS_REFERENCE } from './FTPEOutremerData';

export default function FTPEOutremerPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-black relative">
      {/* Filigrane dynamique des territoires - Visible sur toute la page */}
      <TerritoiresWatermark />
      
      {/* Bouton Retour */}
      <div className="absolute top-4 left-4 z-20">
        <BackButton variant="dark" label="Retour" />
      </div>
      
      {/* Hero Section */}
      <section className="relative overflow-hidden min-h-[600px]">
        {/* Background Effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-green-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:py-24">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            {/* Logo */}
            <div className="flex-shrink-0">
              <img 
                src={FTPE_LOGO} 
                alt="FTPE Outremer - Fédération des Très Petites Entreprises" 
                className="w-80 h-auto drop-shadow-2xl"
              />
            </div>

            {/* Content */}
            <div className="text-center lg:text-left">
              <Badge className="mb-4 bg-green-500/20 text-green-400 border-green-500/30 px-4 py-1">
                <Star className="w-4 h-4 mr-2" />
                Syndicat Patronal
              </Badge>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
                Fédération des{' '}
                <span className="bg-gradient-to-r from-green-400 to-emerald-500 bg-clip-text text-transparent">
                  Très Petites Entreprises
                </span>
                {' '}Outremer
              </h1>
              
              <p className="text-2xl text-green-300 font-medium mb-6">
                Syndicat patronal des employeurs ultramarins
              </p>

              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                <Button 
                  onClick={() => navigate('/adhesion-ftpe-workflow')}
                  className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold rounded-xl shadow-lg shadow-green-500/25"
                >
                  Adhérer à la FTPE Outremer
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/contact')}
                  className="px-8 py-3 border-white/30 text-white hover:bg-white/10 rounded-xl"
                >
                  Nous contacter
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Qui sommes-nous ? */}
      <section className="py-20 border-t border-white/10">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-blue-500/20 text-blue-400 border-blue-500/30">
              <Landmark className="w-4 h-4 mr-2" />
              Qui sommes-nous ?
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Notre identité
            </h2>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              La <strong className="text-green-400">FTPE Outremer</strong> est un syndicat patronal ultramarin 
              dédié à la représentation, à la défense et à l'accompagnement des 
              <strong className="text-green-400"> Très Petites Entreprises (TPE)</strong> et des employeurs 
              individuels des territoires d'outre-mer.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed">
              <strong className="text-white">Indépendante, apolitique et non lucrative</strong>, la Fédération 
              agit pour faire entendre la voix des entrepreneurs ultramarins auprès des pouvoirs publics, 
              des institutions et des partenaires sociaux, en tenant compte des réalités économiques, 
              sociales et territoriales propres aux Outre-mer.
            </p>
          </div>
        </div>
      </section>

      {/* Notre mission */}
      <section className="py-20 bg-gradient-to-b from-black/50 to-transparent">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
              <Target className="w-4 h-4 mr-2" />
              Notre mission
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Ce pour quoi nous agissons
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {MISSIONS.map((mission, idx) => (
              <Card 
                key={idx}
                className="group bg-white/5 border-white/10 hover:bg-white/10 hover:border-green-500/30 transition-all duration-300"
              >
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
                    <mission.icon className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-gray-200 text-lg leading-relaxed pt-2">{mission.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Nos valeurs */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-purple-500/20 text-purple-400 border-purple-500/30">
              <Heart className="w-4 h-4 mr-2" />
              Nos valeurs
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Les piliers de notre action
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALEURS.map((valeur, idx) => (
              <div 
                key={idx}
                className="p-6 rounded-2xl bg-white/5 border border-white/10 text-center hover:bg-white/10 hover:border-purple-500/30 transition-all group"
              >
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-violet-500/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <valeur.icon className="w-8 h-8 text-purple-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{valeur.label}</h3>
                <p className="text-gray-400">{valeur.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* À qui s'adresse la FTPE ? */}
      <section className="py-20 bg-gradient-to-b from-transparent to-black/50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-amber-500/20 text-amber-400 border-amber-500/30">
              <Users className="w-4 h-4 mr-2" />
              À qui s'adresse la FTPE Outremer ?
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Nos publics
            </h2>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {PUBLICS_CIBLES.map((public_, idx) => (
              <div 
                key={idx}
                className="flex items-center gap-3 px-6 py-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-500/30 transition-all"
              >
                <public_.icon className="w-6 h-6 text-amber-400" />
                <span className="text-white font-medium">{public_.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pourquoi adhérer ? */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Avantages */}
            <div>
              <Badge className="mb-4 bg-cyan-500/20 text-cyan-400 border-cyan-500/30">
                <Award className="w-4 h-4 mr-2" />
                Pourquoi adhérer ?
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8">
                Les avantages de l'adhésion
              </h2>

              <div className="space-y-4">
                {AVANTAGES_ADHESION.map((avantage, idx) => (
                  <div 
                    key={idx}
                    className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/10"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-gray-200 text-lg pt-0.5">{avantage}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 rounded-2xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-300">Cotisation annuelle</p>
                    <p className="text-4xl font-bold text-white">200 €</p>
                  </div>
                  <Button 
                    onClick={() => navigate('/adhesion-ftpe-workflow')}
                    className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold"
                  >
                    Adhérer maintenant
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Processus d'adhésion */}
            <div>
              <Badge className="mb-4 bg-rose-500/20 text-rose-400 border-rose-500/30">
                <ClipboardCheck className="w-4 h-4 mr-2" />
                Adhésion
              </Badge>
              <h2 className="text-3xl font-bold text-white mb-6">
                Un processus simple et sécurisé
              </h2>
              <p className="text-gray-400 mb-8">
                L'adhésion s'effectue en ligne, en toute simplicité :
              </p>

              <div className="space-y-4">
                {ETAPES_ADHESION.map((etape, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all"
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center text-white font-bold flex-shrink-0">
                      {etape.numero}
                    </div>
                    <div className="flex items-center gap-3 flex-1">
                      <etape.icon className="w-5 h-5 text-rose-400" />
                      <div>
                        <p className="font-semibold text-white">{etape.titre}</p>
                        <p className="text-sm text-gray-400">{etape.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Documents de référence */}
      <section className="py-16 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-slate-500/20 text-slate-400 border-slate-500/30">
              <FileText className="w-4 h-4 mr-2" />
              Documents de référence
            </Badge>
            <p className="text-gray-400">
              Documents accessibles aux adhérents et téléchargeables lors de l'adhésion
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            {DOCUMENTS_REFERENCE.map((doc, idx) => (
              <div 
                key={idx}
                className="p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all flex items-center gap-3"
              >
                <doc.icon className="w-5 h-5 text-gray-400" />
                <span className="text-gray-300">{doc.titre}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Condition d'accès aux dispositifs et services */}
      <section className="py-16 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-br from-green-900/30 via-emerald-900/20 to-teal-900/30 backdrop-blur-xl rounded-3xl p-8 sm:p-12 border border-green-500/20">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-xl bg-green-500/20">
                <Shield className="w-6 h-6 text-green-400" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white">
                Condition d'accès aux dispositifs et services
              </h2>
            </div>
            
            <div className="space-y-4 text-gray-300">
              <p className="text-lg leading-relaxed">
                L'adhésion à la <strong className="text-green-400">FTPE Outremer</strong> constitue le cadre institutionnel
                requis pour accéder aux dispositifs solidaires portés par <strong className="text-green-400">COPPAM</strong>{' '}
                ainsi qu'aux services proposés au sein de l'écosystème <strong className="text-green-400">O'SCOP</strong>.
              </p>
              <p className="text-lg leading-relaxed">
                Chaque garantie ou service reste soumis à ses conditions propres,
                à une instruction dédiée et, le cas échéant, à une contribution spécifique.
              </p>
            </div>
            
            <div className="mt-6 flex flex-wrap gap-3">
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30 px-4 py-2">
                <CheckCircle className="w-4 h-4 mr-2" />
                Cadre institutionnel requis
              </Badge>
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 px-4 py-2">
                <FileText className="w-4 h-4 mr-2" />
                Conditions propres à chaque service
              </Badge>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-green-600 via-emerald-600 to-teal-600 p-10 sm:p-16">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
            </div>

            <div className="relative z-10 text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Rejoignez la FTPE Outremer
              </h2>
              <p className="text-lg text-green-100 mb-8 max-w-2xl mx-auto">
                Faites entendre votre voix et participez à la défense des intérêts 
                des entrepreneurs ultramarins.
              </p>

              <Button 
                onClick={() => navigate('/adhesion-ftpe-workflow')}
                className="px-10 py-4 bg-white text-green-700 hover:bg-green-50 font-semibold rounded-xl shadow-lg text-lg"
              >
                <Award className="w-5 h-5 mr-2" />
                Adhérer à la FTPE Outremer
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="text-center md:text-left">
              <img 
                src={FTPE_LOGO} 
                alt="FTPE Outremer" 
                className="w-48 h-auto mb-4 mx-auto md:mx-0"
              />
              <p className="text-gray-400 text-sm">
                Fédération des Très Petites Entreprises Outremer<br />
                Syndicat patronal des employeurs ultramarins
              </p>
            </div>

            <div className="text-center md:text-right">
              <h4 className="text-white font-semibold mb-4">Contact</h4>
              <div className="space-y-3 text-gray-400">
                <p className="font-medium text-white">FTPE Outremer</p>
                <div className="flex items-center justify-center md:justify-end gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>Baie-Mahault – Guadeloupe</span>
                </div>
                <div className="flex items-center justify-center md:justify-end gap-2">
                  <Mail className="w-4 h-4" />
                  <a href="mailto:contact@ftpe-outremer.fr" className="hover:text-green-400 transition-colors">
                    contact@ftpe-outremer.fr
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/10 text-center text-gray-500 text-sm">
            © {new Date().getFullYear()} FTPE Outremer - Tous droits réservés
          </div>
        </div>
      </section>
    </div>
  );
}
